import { onMounted, ref, useTemplateRef, type Ref, type ShallowRef } from 'vue'
import { useEventListener } from '@vueuse/core'
import {
    getDocument,
    getRequestAnimationFrame,
    getTimeout,
} from '@/helpers/globals'

// step is the number of pixels the mouse travels before the image changes
export function useThreeSixtyInput(
    currentImageIndex: Ref<number>,
    step = ref(10)
): {
    handleTouchStart: (e: TouchEvent) => void
    handleTouchMove: (e: TouchEvent) => void
    handleMouseDown: (e: MouseEvent) => void
    inputCatcherEl: Readonly<ShallowRef<HTMLElement | null>>
} {
    let xStart = 0
    let xEnd = 0
    let initialImageIndex = 0

    const handleStart = (x: number | undefined) => {
        if (typeof x !== 'number') {
            return
        }

        xStart = x
        xEnd = x
        initialImageIndex = currentImageIndex.value
    }

    const handleMove = (x: number | undefined): void => {
        if (typeof x !== 'number') {
            return
        }

        xEnd = x

        const diff = xEnd - xStart

        currentImageIndex.value =
            Math.round(diff / step.value) + initialImageIndex
    }

    const handleTouchStart = (e: TouchEvent): void => {
        handleStart(e.touches[0]?.clientX)
    }

    const handleTouchMove = (e: TouchEvent): void => {
        handleMove(e.touches[0]?.clientX)
    }

    const handleMouseDown = (e: MouseEvent): void => {
        handleStart(e.clientX)

        const document = getDocument()

        if (document) {
            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener(
                'mouseup',
                () => {
                    document.removeEventListener('mousemove', handleMouseMove)
                },
                {
                    once: true,
                }
            )
        }
    }

    const handleMouseMove = (e: MouseEvent): void => {
        handleMove(e.clientX)
    }

    const requestAnimationFrame = getRequestAnimationFrame()
    const setTimeout = getTimeout()?.setTimeout
    const inputCatcherEl = useTemplateRef<HTMLElement>('inputCatcherEl')
    let isResetting = false
    let isFirstScroll = true
    let scrollInitial = 200

    const resetScrollPosition = () => {
        if (!inputCatcherEl.value) {
            return
        }

        isResetting = true
        isFirstScroll = true

        inputCatcherEl.value.scrollTo({
            behavior: 'instant',
            top: 0,
            left: scrollInitial,
        })
    }

    onMounted(() => {
        const el = inputCatcherEl.value

        if (!el) {
            return
        }

        scrollInitial = el.scrollWidth / 2

        resetScrollPosition()
    })

    useEventListener(inputCatcherEl, 'scroll', () => {
        if (isResetting) {
            return
        }

        const scrollLeft = inputCatcherEl.value?.scrollLeft

        if (isFirstScroll) {
            isFirstScroll = false
            handleStart(scrollLeft)
        }

        requestAnimationFrame(() => handleMove(scrollLeft))
    })

    useEventListener(inputCatcherEl, 'scrollend', () => {
        if (isResetting) {
            isResetting = false

            return
        }

        setTimeout?.(resetScrollPosition)
    })

    return {
        handleTouchStart,
        handleTouchMove,
        handleMouseDown,
        inputCatcherEl,
    }
}
